import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getHelpPages } from '../../../models/helpModel';
import { getSettings } from '../../../models/settingsModel';

function PublicHelpViewer() {
  const { path } = useParams();
  const navigate = useNavigate();
  const [helpPages, setHelpPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchHelpPages();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsResponse = await getSettings();
      if (settingsResponse && settingsResponse.status === 1 && settingsResponse.data) {
        setSettings(settingsResponse.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  useEffect(() => {
    if (helpPages.length > 0 && path) {
      const page = helpPages.find(p => p.help_path === path);
      console.log('Looking for path:', path);
      console.log('Found page:', page);
      if (page) {
        setCurrentPage(page);
      } else {
        setError('Help page not found');
      }
    }
  }, [helpPages, path]);

  const fetchHelpPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getHelpPages();
      console.log('Help pages API response:', response);
      if (response.status === 1 || response.status === 'success') {
        // Only show active help pages with valid paths
        // Filter: help_status == 0 or null, AND help_path is not empty
        const activePages = (response.data?.help_pages || []).filter(p => 
          (p.help_status == 0 || p.help_status == null) && 
          p.help_path && 
          p.help_path.trim() !== ''
        );
        console.log('Active help pages:', activePages);
        setHelpPages(activePages);
      } else {
        console.error('API returned error:', response);
        setError('Failed to load help pages');
      }
    } catch (error) {
      console.error('Error fetching help pages:', error);
      setError('Failed to load help pages');
    } finally {
      setLoading(false);
    }
  };

  const groupPagesBySection = () => {
    const grouped = {};
    helpPages.forEach(page => {
      const section = page.help_section || 'General';
      if (!grouped[section]) {
        grouped[section] = [];
      }
      grouped[section].push(page);
    });
    return grouped;
  };

  const handlePageClick = (pagePath) => {
    navigate(`/help/${pagePath}`);
  };

  const handleBackToList = () => {
    navigate('/help');
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                {/* Company Logo */}
                {settings?.site_logo_preview && (
                  <img 
                    src={settings.site_logo_preview} 
                    alt="Company Logo" 
                    className="h-10 w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="mt-4 text-gray-600">Loading help...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !path) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                {/* Company Logo */}
                {settings?.site_logo_preview && (
                  <img 
                    src={settings.site_logo_preview} 
                    alt="Company Logo" 
                    className="h-10 w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <div className="text-center p-8">
                <div className="text-red-600 text-5xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Help</h1>
                <p className="text-gray-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show help page list if no path specified
  if (!path) {
    const groupedPages = groupPagesBySection();

    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                {/* Company Logo */}
                {settings?.site_logo_preview && (
                  <img 
                    src={settings.site_logo_preview} 
                    alt="Company Logo" 
                    className="h-10 w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Help Center Title Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6" style={{ background: 'var(--gradient-primary)' }}>
              <h3 className="text-lg leading-6 font-medium text-white">
                Help Center
              </h3>
              <p className="mt-1 text-sm text-white opacity-90">
                Browse our help articles and guides
              </p>
            </div>
          </div>

          {/* Help Topics */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-6">
              {Object.keys(groupedPages).length === 0 ? (
                <p className="text-gray-500">No help pages available.</p>
              ) : (
                <div className="space-y-8">
                  {Object.entries(groupedPages).map(([section, pages]) => (
                    <div key={section}>
                      <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">
                        {section}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pages.map(page => (
                          <button
                            key={page.help_id}
                            onClick={() => handlePageClick(page.help_path)}
                            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
                          >
                            <h3 className="font-medium text-blue-600 hover:text-blue-700">
                              {page.help_title}
                            </h3>
                            {page.help_text && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {page.help_text.replace(/<[^>]*>/g, '').substring(0, 100)}...
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show specific help page
  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                {/* Company Logo */}
                {settings?.site_logo_preview && (
                  <img 
                    src={settings.site_logo_preview} 
                    alt="Company Logo" 
                    className="h-10 w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-6">
              <button
                onClick={handleBackToList}
                className="mb-6 text-blue-600 hover:text-blue-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Help Center
              </button>
              <div className="text-center py-12">
                <div className="text-6xl mb-4">404</div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h1>
                <p className="text-gray-600">The help page you're looking for doesn't exist or has been moved.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              {/* Company Logo */}
              {settings?.site_logo_preview && (
                <img 
                  src={settings.site_logo_preview} 
                  alt="Company Logo" 
                  className="h-10 w-auto"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Title Card */}
        {currentPage && (
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:px-6" style={{ background: 'var(--gradient-primary)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg leading-6 font-medium text-white">
                      {currentPage.help_title}
                    </h3>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-white text-blue-800">
                      {currentPage.help_section || 'General'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-6">
            <button
              onClick={handleBackToList}
              className="mb-6 text-blue-600 hover:text-blue-700 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Help Center
            </button>

            {currentPage && (
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: currentPage.help_text }}
              />
            )}
          </div>
        </div>

        {/* Related pages */}
        {currentPage && helpPages.filter(p => 
          p.help_section === currentPage.help_section && 
          p.help_id !== currentPage.help_id
        ).length > 0 && (
          <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Related Help Topics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {helpPages
                  .filter(p => 
                    p.help_section === currentPage.help_section && 
                    p.help_id !== currentPage.help_id
                  )
                  .slice(0, 4)
                  .map(page => (
                    <button
                      key={page.help_id}
                      onClick={() => handlePageClick(page.help_path)}
                      className="text-left p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow transition-all"
                    >
                      <h3 className="text-sm font-medium text-blue-600 hover:text-blue-700">
                        {page.help_title}
                      </h3>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicHelpViewer;
